/**
 * OpenMeteoWeatherProvider.ts — LOCAL INTELLIGENCE™ (Build 2.04.1).
 *
 * The first LIVE fusion provider: real weather from the Open-Meteo public API
 * (no API key required). Registered at priority 10 so it wins over the
 * placeholder mock (priority 100) in the fallback chain — if the network or
 * API fails, the engine falls back exactly as the fusion architecture
 * prescribes (next provider → stale cache → empty) and the failure is
 * recorded in provider health. No fallback constants live here.
 *
 * Fusion is the ONLY layer allowed to touch external APIs — UI and engines
 * consume this exclusively through roamerDataFusionEngine.fetch('weather', …).
 */

import type { FusedWeather, WeatherCondition } from '../models';
import type { FusionProvider, FusionQuery } from './types';
import { InvalidResponseError, NetworkError, ProviderUnavailableError } from '../errors';

export const OPEN_METEO_PROVIDER_ID = 'open-meteo';

/** WMO weather interpretation codes → WorkRoamer conditions (standard table). */
export function wmoCodeToCondition(code: number): WeatherCondition {
  if (code === 0) return 'clear';
  if (code === 1 || code === 2) return 'partly-cloudy';
  if (code === 3 || code === 45 || code === 48) return 'cloudy';
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return 'rain';
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return 'snow';
  if (code >= 95) return 'storm';
  return 'cloudy';
}

function asNum(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export const openMeteoWeatherProvider: FusionProvider<'weather'> = {
  meta: {
    id: OPEN_METEO_PROVIDER_ID,
    name: 'Open-Meteo (live)',
    domain: 'weather',
    live: true,
    priority: 10,
    baseConfidence: 92,
  },

  async fetch(query: FusionQuery): Promise<FusedWeather[]> {
    const { latitude, longitude } = query.location;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      throw new ProviderUnavailableError('location has no coordinates', OPEN_METEO_PROVIDER_ID);
    }
    const url =
      'https://api.open-meteo.com/v1/forecast' +
      `?latitude=${latitude.toFixed(4)}&longitude=${longitude.toFixed(4)}` +
      '&current=temperature_2m,apparent_temperature,relative_humidity_2m,weather_code' +
      '&daily=temperature_2m_max,temperature_2m_min,sunrise,sunset' +
      '&hourly=precipitation_probability' +
      '&forecast_days=1&timezone=auto';

    let res: Response;
    try {
      res = await fetch(url);
    } catch (e) {
      throw new NetworkError(e instanceof Error ? e.message : 'network error', OPEN_METEO_PROVIDER_ID);
    }
    if (!res.ok) {
      throw new InvalidResponseError(`HTTP ${res.status}`, OPEN_METEO_PROVIDER_ID);
    }
    const body = (await res.json()) as Record<string, unknown>;
    const current = (body.current ?? {}) as Record<string, unknown>;
    const daily = (body.daily ?? {}) as Record<string, unknown[]>;
    const hourly = (body.hourly ?? {}) as Record<string, unknown[]>;

    const tempC = asNum(current.temperature_2m);
    const code = asNum(current.weather_code);
    const highC = asNum(daily.temperature_2m_max?.[0]);
    const lowC = asNum(daily.temperature_2m_min?.[0]);
    const humidity = asNum(current.relative_humidity_2m);
    if (tempC == null || code == null || highC == null || lowC == null || humidity == null) {
      // Incomplete response = soft failure; the engine moves down the chain.
      // We never patch holes with invented numbers.
      return [];
    }

    // First hour of the local day with ≥50% precipitation probability, or null.
    let rainStartHour: number | null = null;
    const probs = Array.isArray(hourly.precipitation_probability) ? hourly.precipitation_probability : [];
    for (let h = 0; h < Math.min(24, probs.length); h++) {
      const p = asNum(probs[h]);
      if (p != null && p >= 50) { rainStartHour = h; break; }
    }

    const feelsLikeC = asNum(current.apparent_temperature);
    // Sunrise/sunset are optional extras — included only when reported.
    const sunriseIso = typeof daily.sunrise?.[0] === 'string' ? (daily.sunrise[0] as string) : null;
    const sunsetIso = typeof daily.sunset?.[0] === 'string' ? (daily.sunset[0] as string) : null;
    return [{
      id: `${OPEN_METEO_PROVIDER_ID}:weather:${query.location.locationId}`,
      location: { latitude, longitude },
      tempC: Math.round(tempC),
      ...(feelsLikeC != null ? { feelsLikeC: Math.round(feelsLikeC) } : {}),
      ...(sunriseIso ? { sunriseIso } : {}),
      ...(sunsetIso ? { sunsetIso } : {}),
      highC: Math.round(highC),
      lowC: Math.round(lowC),
      humidityPct: Math.round(humidity),
      condition: wmoCodeToCondition(code),
      rainStartHour,
      // meta is replaced by the engine's quality layer.
      meta: { confidence: 0, lastUpdated: 0, providerSource: OPEN_METEO_PROVIDER_ID, verificationStatus: 'unverified' },
    }];
  },
};
